import React, { useEffect } from "react";
import { Form, Button, Modal, Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import {
  createNotification,
  fetchNotificationByUserId,
  fetchNotifications,
} from "../../../../store/features/notification/notificationSlice";
import {
  fetchAssistantSuperadmin,
  fetchManagers,
  fetchStaffs,
  fetchUserById,
} from "../../../../store/features/user/userSlice";
import { toast } from "react-toastify";

const NotificationModal = ({
  showForm,
  setShowForm,
  editingNotification,
  setEditingNotification,
}) => {
  const { usersCreatedBy, assistantSuperAdmin, managers, staffs, users } =
    useSelector((state) => state.users);
  const { role, _id } = useSelector((state) => state.auth);

  const dispatch = useDispatch();

  const initialValues = {
  assistantSuperAdminIds: editingNotification?.assistantSuperAdminIds || [],
  managerIds: editingNotification?.managerIds || [],
  staffIds: editingNotification?.staffIds || [],
  auditorIds: editingNotification?.auditorIds || [],
  message: editingNotification?.message || "",
  isRead: editingNotification?.isRead ?? false,
};

  const validationSchema = Yup.object().shape({
    message: Yup.string().required("Message is required"),
  });

  useEffect(() => {
    if (role === "superadmin") {
      dispatch(fetchAssistantSuperadmin());
      dispatch(fetchManagers());
      dispatch(fetchStaffs());
    } else {
      dispatch(fetchUserById(_id));
    }
  }, [dispatch, role, _id]);

  const assistantOptions =
    assistantSuperAdmin?.map((u) => ({ label: u.name, value: u._id })) || [];

  const managerOptions =
    (role === "superadmin" || role === "auditor"
      ? managers
      : usersCreatedBy[0]?.createdUsers?.filter((u) => u.role === "manager")
    )?.map((u) => ({ label: u.name, value: u._id })) || [];

  const staffOptions =
    (role === "superadmin" || role === "auditor"
      ? staffs
      : usersCreatedBy[0]?.createdUsers?.filter((u) => u.role === "staff")
    )?.map((u) => ({ label: u.name, value: u._id })) || [];

  const auditorOptions =
    (role === "superadmin"
      ? users[0]?.children?.filter((u) => u.role === "auditor")
      : []
    )?.map((u) => ({ label: u.name, value: u._id })) || [];

  return (
    <Modal size="lg" show={showForm} onHide={() => setShowForm(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editingNotification?.message ? "Edit Notification" : "Send Notification"}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={async (values, { resetForm }) => {
          try {
            values.role = role;
            await dispatch(createNotification(values)).unwrap();
            if (role === "superadmin") {
              dispatch(fetchNotifications());
            } else {
              dispatch(fetchNotificationByUserId());
            }
            toast.success("Notifications created!");
            resetForm();
            setShowForm(false);
            setEditingNotification(null);
          } catch (err) {
            toast.error("Error saving Notification");
          }
        }}
      >
        {({
          values,
          setFieldValue,
          handleSubmit,
          touched,
          errors,
          isSubmitting,
          validateForm,
          setTouched,
        }) => {
          const handleMultiSelectChange = (fieldName, selectedOptions) => {
            setFieldValue(
              fieldName,
              selectedOptions ? selectedOptions.map((opt) => opt.value) : []
            );
          };

          const getSelectedOptions = (fieldName, options) => {
            return options.filter((opt) => values[fieldName]?.includes(opt.value));
          };

          const handleSelectAll = (fieldName, options, isChecked) => {
            setFieldValue(
              fieldName,
              isChecked ? options.map((opt) => opt.value) : []
            );
          };

          const handleFormSubmit = async (e) => {
            e.preventDefault();
            const formErrors = await validateForm();
            if (Object.keys(formErrors).length > 0) {
              setTouched(
                Object.keys(formErrors).reduce((acc, key) => {
                  acc[key] = true;
                  return acc;
                }, {})
              );
              return;
            }
            handleSubmit();
          };

          return (
            <Form onSubmit={handleFormSubmit}>
              <Modal.Body>
                <Row>
                  {role === "superadmin" && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Assistant Superadmin</Form.Label>
                        <div className="mb-2">
                          <Form.Check
                            type="checkbox"
                            label="Select All"
                            checked={
                              values.assistantSuperAdminIds?.length === assistantOptions.length &&
                              assistantOptions.length > 0
                            }
                            onChange={(e) =>
                              handleSelectAll("assistantSuperAdminIds", assistantOptions, e.target.checked)
                            }
                          />
                        </div>
                        <Select
                          options={assistantOptions}
                          isMulti
                          closeMenuOnSelect={false}
                          value={getSelectedOptions("assistantSuperAdminIds", assistantOptions)}
                          onChange={(selected) =>
                            handleMultiSelectChange("assistantSuperAdminIds", selected)
                          }
                          placeholder="Select Assistant Superadmins..."
                        />
                      </Form.Group>
                    </Col>
                  )}

                  {role !== "manager" && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Manager</Form.Label>
                        <div className="mb-2">
                          <Form.Check
                            type="checkbox"
                            label="Select All"
                            checked={
                              values.managerIds?.length === managerOptions.length &&
                              managerOptions.length > 0
                            }
                            onChange={(e) =>
                              handleSelectAll("managerIds", managerOptions, e.target.checked)
                            }
                          />
                        </div>
                        <Select
                          options={managerOptions}
                          isMulti
                          closeMenuOnSelect={false}
                          value={getSelectedOptions("managerIds", managerOptions)}
                          onChange={(selected) =>
                            handleMultiSelectChange("managerIds", selected)
                          }
                          placeholder="Select Managers..."
                        />
                      </Form.Group>
                    </Col>
                  )}

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Staff</Form.Label>
                      <div className="mb-2">
                        <Form.Check
                          type="checkbox"
                          label="Select All"
                          checked={
                            values.staffIds?.length === staffOptions.length &&
                            staffOptions.length > 0
                          }
                          onChange={(e) =>
                            handleSelectAll("staffIds", staffOptions, e.target.checked)
                          }
                        />
                      </div>
                      <Select
                        options={staffOptions}
                        isMulti
                        closeMenuOnSelect={false}
                        value={getSelectedOptions("staffIds", staffOptions)}
                        onChange={(selected) =>
                          handleMultiSelectChange("staffIds", selected)
                        }
                        placeholder="Select Staff..."
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Auditor</Form.Label>
                      <div className="mb-2">
                        <Form.Check
                          type="checkbox"
                          label="Select All"
                          checked={
                            values.auditorIds?.length === auditorOptions.length &&
                            auditorOptions.length > 0
                          }
                          onChange={(e) =>
                            handleSelectAll("auditorIds", auditorOptions, e.target.checked)
                          }
                        />
                      </div>
                      <Select
                        options={auditorOptions}
                        isMulti
                        closeMenuOnSelect={false}
                        value={getSelectedOptions("auditorIds", auditorOptions)}
                        onChange={(selected) =>
                          handleMultiSelectChange("auditorIds", selected)
                        }
                        placeholder="Select Auditors..."
                      />
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Message <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        name="message"
                        value={values.message}
                        onChange={(e) => setFieldValue("message", e.target.value)}
                        isInvalid={touched.message && errors.message}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {editingNotification?.message ? "Update" : "Create"}
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default NotificationModal;